import { registerPlugin } from '@capacitor/core';
import { Share } from '@capacitor/share';

// Import the CustomSharePlugin (native iOS implementation)
const CustomShare = registerPlugin('CustomShare', {
  web: () => import('../web/customShare.web').then(m => new m.CustomShareWeb()),
});

/**
 * iOS share implementation using CustomSharePlugin
 * Falls back to Capacitor Share plugin if CustomShare is not available
 * Supports excludedActivityTypes for better control
 */
export async function share(options) {
  // Check if CustomShare plugin is implemented by trying to call it
  // If it throws UNIMPLEMENTED error, use Capacitor Share directly
  try {
    console.log("[iOS Share] Attempting to use CustomShare plugin with options:", options);
    
    // Set flag to indicate native code will handle logging (if plugin works)
    // This prevents double logging
    if (typeof window !== 'undefined') {
      window.__shareAmplitudeLogged = false;
    }
    
    // Try to call CustomShare - if it's not implemented, it will throw UNIMPLEMENTED
    const result = await CustomShare.share(options);
    console.log("[iOS Share] CustomShare.share result:", result);
    console.log("[iOS Share] result.activityType:", result?.activityType);
    console.log("[iOS Share] result keys:", result ? Object.keys(result) : "result is null/undefined");
    console.log("[iOS Share] Full result JSON:", JSON.stringify(result));
    
    // If user cancelled, don't throw an error - just return the result
    if (result && result.cancelled) {
      console.log("[iOS Share] User cancelled share dialog");
      return result;
    }
    
    // If native code logged (check flag), mark it so WordHit.jsx doesn't log again
    // Wait a bit for native code to execute
    await new Promise(resolve => setTimeout(resolve, 100));
    if (typeof window !== 'undefined' && window.__shareAmplitudeLogged) {
      console.log("[iOS Share] ✅ Native code already logged to Amplitude");
      result.__amplitudeLogged = true; // Flag to prevent double logging in WordHit.jsx
    }
    
    // Pass through activityType if available (for tracking which platform was used)
    return result;
  } catch (error) {
    // IMPORTANT: Even if we get UNIMPLEMENTED error, the native code might still execute
    // and return activityType via the bridge. We need to check if activityType is available
    // in the error or wait for it to come through the bridge.
    // For now, we'll fall back to Capacitor Share, but note that activityType won't be available
    console.log("[iOS Share] Error caught:", error);
    console.log("[iOS Share] Error code:", error.code);
    console.log("[iOS Share] Error message:", error.message);
    console.log("[iOS Share] Error name:", error.name);
    
    // Check if error is UNIMPLEMENTED or not implemented
    const isUnimplemented = error.code === 'UNIMPLEMENTED' || 
                           error.message?.includes('not implemented') ||
                           error.message?.includes('UNIMPLEMENTED') ||
                           error.message?.includes('No native implementation');
    
    if (isUnimplemented) {
      console.log("[iOS Share] CustomShare plugin not implemented, using Capacitor Share plugin");
      console.log("[iOS Share] ⚠️ NOTE: Native code might still execute and send activityType via bridge");
      console.log("[iOS Share] We'll try to capture it from the TO JS message");
      
      // Use Capacitor Share plugin as fallback
      const shareData = {};
      if (options.title) shareData.title = options.title;
      if (options.text) shareData.text = options.text;
      if (options.url) shareData.url = options.url;
      
      // IMPORTANT: Even though the plugin is "UNIMPLEMENTED", the native code might still run
      // and send activityType via the bridge. We need to set up a listener BEFORE calling Share.share
      
      // Set up listener to capture activityType from bridge messages
      // The native code might still run and send activityType via bridge even if plugin isn't recognized
      let capturedActivityType = null;
      let hasLoggedToAmplitude = false;
      
      // Don't override console methods - they can cause crashes
      // Instead, just poll for native variables that Swift code sets
      
      try {
        // Clear any previous activityType from global variable
        // Set the event name so the global listener knows which event to track
        if (typeof window !== 'undefined') {
          window.__lastShareActivityType = null;
          window.__shareAmplitudeLogged = false;
          window.__currentShareEventName = options.amplitudeEventName || 'share_score_clicked';
          // Clear native variables
          window.__nativeActivityType = null;
          window.__nativeEventName = null;
          window.__nativeActivityTypeTimestamp = null;
        }
        
        // Set up a promise that resolves when we get activityType
        let activityTypeResolver = null;
        const activityTypePromise = new Promise((resolve) => {
          activityTypeResolver = resolve;
        });
        
        // Set up a one-time listener for the TO JS message
        // We'll check the console output directly
        const checkForActivityType = () => {
          // Check if window has the native variables (set by Swift)
          if (typeof window !== 'undefined' && window.__nativeActivityType) {
            const activityType = window.__nativeActivityType;
            const eventName = window.__nativeEventName || options.amplitudeEventName || 'share_score_clicked';
            
            if (activityTypeResolver) {
              activityTypeResolver({ activityType, eventName });
              activityTypeResolver = null;
            }
            return true;
          }
          return false;
        };
        
        // Start polling immediately
        const pollInterval = setInterval(() => {
          if (checkForActivityType()) {
            clearInterval(pollInterval);
          }
        }, 100);
        
        // Also check after Share.share completes
        await Share.share(shareData);
        
        console.log("[iOS Share] Share completed, polling for activityType...");
        
        // Poll for activityType - check native variables that Swift code sets
        const startTime = Date.now();
        const maxWaitTime = 5000; // 5 seconds
        
        while (Date.now() - startTime < maxWaitTime && !hasLoggedToAmplitude) {
          await new Promise(resolve => setTimeout(resolve, 150));
          
          // Check if already logged
          if (typeof window !== 'undefined' && window.__shareAmplitudeLogged) {
            console.log("[iOS Share] ✅ Already logged to Amplitude");
            hasLoggedToAmplitude = true;
            break;
          }
          
          // Check native variables
          if (typeof window !== 'undefined' && window.__nativeActivityType) {
            const activityType = window.__nativeActivityType;
            const eventName = window.__nativeEventName || options.amplitudeEventName || 'share_score_clicked';
            
            console.log("[iOS Share] ✅ Found __nativeActivityType:", activityType);
            capturedActivityType = activityType;
            
            // Log to Amplitude immediately
            if (!hasLoggedToAmplitude && window.amplitude) {
              try {
                window.amplitude.track(eventName, {
                  platform: 'ios',
                  activityType: activityType
                });
                hasLoggedToAmplitude = true;
                window.__shareAmplitudeLogged = true;
                console.log("[iOS Share] ✅ Logged to Amplitude:", eventName, "with activityType:", activityType);
                break;
              } catch (amplitudeError) {
                console.warn("[iOS Share] Failed to log to Amplitude:", amplitudeError);
              }
            }
          }
        }
        
        if (!hasLoggedToAmplitude) {
          console.log("[iOS Share] ⚠️ Timeout. window.__nativeActivityType:", typeof window !== 'undefined' ? window.__nativeActivityType : 'N/A');
        }
        
        // Return result with captured activityType if available
        const result = { presented: true };
        if (capturedActivityType) {
          result.activityType = capturedActivityType;
          console.log("[iOS Share] ✅ Added captured activityType to result:", capturedActivityType);
        } else {
          console.log("[iOS Share] ⚠️ No activityType captured from bridge");
        }
        // Set flag if we logged to prevent double logging in WordHit.jsx
        if (hasLoggedToAmplitude) {
          result.__amplitudeLogged = true;
        }
        return result;
      } catch (shareError) {
        console.error("[iOS Share] Capacitor Share also failed:", shareError);
        throw shareError;
      }
    }
    
    // For other errors (like user cancellation), check if it's a cancellation
    if (error.message?.includes('cancel') || error.name === 'AbortError') {
      console.log("[iOS Share] User cancelled share");
      return { cancelled: true };
    }
    
    // Re-throw other errors
    console.error("[iOS Share] Unexpected error:", error);
    throw error;
  }
}
